import { useState, useEffect } from 'react'
import s from './ReportByWorkType.scss'
import { makeReportsPostRequest, makeGetRequest, getReportsBaseUrl } from 'utils/api'
import { useLoading } from 'hooks'
import Select from 'react-select'
import { RangeDatePicker } from 'components/ideal-core-ui'
import { utcDate, timezoneDate } from 'utils/helpers'
import moment from 'moment'
import { toUsers } from '../serializers'
import { groupDataByUser } from '../utils'
import { isTimetrackerAdmin, getToken } from 'utils/authService'
import { AddButton } from 'components/common/AddButton'
import { ProgressBar } from 'components/common/ProgressBar'
import { FaFileExcel, FaAngleDown, FaAngleUp } from 'react-icons/fa'
import { UserTable } from '../UserTable'

const ReportByWorkType = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  handleDatesShortcut,
  setDateRangeOption,
  dateRangeOption
}) => {
  const [, withLoadingLocks] = useLoading()
  const [userFilter, setUserFilter] = useState([])
  const [result, setResult] = useState([])
  const [users, setUsers] = useState([])
  const [expandedSections, setExpandedSections] = useState([])
  const [usersToExpand, setUsersToExpand] = useState([])

  const setDateFilters = (dates, utc = true) => {
    setStartDate(utc ? utcDate(dates.from) : dates.from)
    setEndDate(utc ? utcDate(dates.to) : dates.to)
  }

  useEffect(() => {
    ;(async () => {
      if (!isTimetrackerAdmin()) return
      const { content } = await makeGetRequest('/v1/timetracker/users')
      setUsers(toUsers(content))
    })()
  }, [])

  useEffect(() => {
    ;(async () => {
      const response = await withLoadingLocks(
        makeReportsPostRequest('/v1/reports/time-tracker/by-service', {
          from: startDate,
          to: endDate,
          ...(userFilter && userFilter.length
            ? { userFilter: userFilter.map(user => user.value) }
            : {})
        })
      )
      setResult(response)
      setExpandedSections(
        Object.keys(response).map(() => {
          return false
        })
      )
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, userFilter])

  const getDateRangeLabel = () => {
    if (!startDate || !endDate) return ''
    const formattedStartDate = timezoneDate(
      startDate,
      undefined,
      undefined,
      undefined,
      'MMMM D, YYYY'
    ).toUpperCase()
    const formattedEndDate = timezoneDate(
      endDate,
      undefined,
      undefined,
      undefined,
      'MMMM D, YYYY'
    ).toUpperCase()

    const showYear = moment(startDate).year() !== moment(endDate).year()

    let dateRange
    if (showYear) {
      dateRange = `${formattedStartDate} - ${formattedEndDate}`
    } else {
      dateRange = `${timezoneDate(
        startDate,
        undefined,
        undefined,
        undefined,
        'MMMM D'
      ).toUpperCase()} - ${timezoneDate(
        endDate,
        undefined,
        undefined,
        undefined,
        'MMMM D, YYYY'
      ).toUpperCase()}`
    }

    return dateRange
  }

  const onBtnExport = () => {
    window.open(
      getReportsBaseUrl(
        `/v1/reports/time-tracker/by-service/export?userFilter=${userFilter
          .map(user => user.value)
          .join()}&token=${getToken()}${'&from=' + startDate + '&to=' + endDate}`
      ),
      '_blank'
    )
  }

  const expandSection = index => {
    const newExpandedSections = [...expandedSections]
    newExpandedSections[index] = !newExpandedSections[index]
    setExpandedSections(newExpandedSections)
  }

  return (
    <div className={s.container}>
      <div className={s.filters}>
        <div className={s.filter}>
          <Select
            className={s.dateRangeSelect}
            onChange={v => {
              handleDatesShortcut(v)
              setDateRangeOption(v)
            }}
            options={[
              { value: 'lastYear', label: 'Last Year' },
              { value: 'thisYear', label: 'This Year' },
              { value: 'lastMonth', label: 'Last Month' },
              { value: 'thisMonth', label: 'This Month' },
              { value: 'lastWeek', label: 'Last Week' },
              { value: 'thisWeek', label: 'This Week' },
              { value: 'custom', label: 'Custom Dates' }
            ]}
            value={dateRangeOption}
            placeholder="Select Date Range"
          />
          {dateRangeOption.value === 'custom' && (
            <RangeDatePicker startDate={startDate} endDate={endDate} onChange={setDateFilters} />
          )}
        </div>
        {isTimetrackerAdmin() && (
          <Select
            onChange={values => setUserFilter(values)}
            options={users}
            value={userFilter}
            placeholder="Select User(s)"
            className={s.userSelect}
            isMulti
          />
        )}
      </div>
      {result && Object.keys(result).filter(Boolean).length ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <p className={s.dateRange}>{getDateRangeLabel()}</p>
            <AddButton className={s.exportExcel} onClick={onBtnExport}>
              <FaFileExcel className={s.excelIcon} />
              Export to Excel
            </AddButton>
          </div>
          <hr />
          {Object.entries(result)
            .filter(([key]) => Boolean(key))
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, value], index) => {
              const timeEntries = groupDataByUser(value.timeTrackerList)

              return (
                <div key={key} className={s.stats}>
                  <p
                    className={s.entriesFor}
                    onClick={() => {
                      expandSection(index)

                      const newObject = Object.keys(timeEntries).reduce((acc, key) => {
                        acc[key] = false
                        return acc
                      }, {})
                      let newUsersToExpand = [...usersToExpand]
                      newUsersToExpand[index] = newObject
                      setUsersToExpand(newUsersToExpand)
                    }}
                  >
                    {key}
                    {expandedSections[index] ? <FaAngleUp /> : <FaAngleDown />}
                  </p>
                  <div
                    style={{ display: 'flex', borderBottom: '1px solid gray', paddingBottom: 20 }}
                  >
                    <div style={{ width: '400px' }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between'
                        }}
                      >
                        <span style={{ fontWeight: 600, color: 'gray' }}>Total hours</span>
                        <span style={{ fontSize: 18, fontWeight: 'bold' }}>
                          {value.totals?.totalHours}
                        </span>
                      </div>
                    </div>
                    <div style={{ flex: 1, padding: '0 40px', position: 'relative', top: 3 }}>
                      <ProgressBar
                        regularHours={
                          value.totals?.billable
                            ? Object.values(value.totals.billable).reduce((a, b) => a + b, 0)
                            : 0
                        }
                        offHours={value.totals?.totalOffHours ?? 0}
                        maxValue={value.totals?.totalHours ?? 0}
                        index={key.replace(/[^a-zA-Z0-9]/g, '')}
                        secondShiftBillableHours={value.totals?.billable.secondShiftHours ?? 0}
                        secondShiftNotBillableHours={value.total?.nonBillable.secondShiftHours ?? 0}
                        thirdShiftBillableHours={value.totals?.billable.thirdShiftHours ?? 0}
                        thirdShiftNotBillableHours={value.totals?.nonBillable.thirdShiftHours ?? 0}
                        overtimeBillableHours={value.totals?.billable.overtimeHours ?? 0}
                        overtimeNotBillableHours={value.totals?.nonBillable.overtimeHours ?? 0}
                        standardBillableHours={value.totals?.billable.regularHours ?? 0}
                        standardNotBillableHours={value.totals?.nonBillable.regularHours ?? 0}
                      />
                    </div>
                  </div>
                  {expandedSections[index] &&
                    Object.entries(timeEntries).map(([key, v]) => {
                      return (
                        <>
                          <div key={key} style={{ display: 'flex', margin: '20px 0' }}>
                            <div style={{ width: '400px', paddingLeft: '20px' }}>
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between'
                                }}
                              >
                                <span
                                  style={{ fontWeight: 600, color: 'black' }}
                                  onClick={() => {
                                    const newUsersToExpand = [...usersToExpand]
                                    newUsersToExpand[index][key] = !newUsersToExpand[index][key]
                                    setUsersToExpand(newUsersToExpand)
                                  }}
                                >
                                  {key}{' '}
                                  {usersToExpand[index][key] ? <FaAngleUp /> : <FaAngleDown />}
                                </span>
                                <span style={{ fontSize: 18, fontWeight: 'bold' }}>
                                  {v.totalHours}
                                </span>
                              </div>
                            </div>
                            <div
                              style={{ flex: 1, padding: '0 40px', position: 'relative', top: 3 }}
                            >
                              <ProgressBar
                                regularHours={v.totalBillableHours}
                                offHours={v.totalNonBillableHours}
                                maxValue={value.totals.totalHours}
                                index={key.replace(/[^a-zA-Z0-9]/g, '')}
                                secondShiftBillableHours={v.totalBillableSecondShiftHours}
                                secondShiftNotBillableHours={v.totalNotBillableSecondShiftHours}
                                thirdShiftBillableHours={v.totalBillableThirdShiftHours}
                                thirdShiftNotBillableHours={v.totalNotBillableThirdShiftHours}
                                overtimeBillableHours={v.totalBillableOvertimeHours}
                                overtimeNotBillableHours={v.totalNotBillableOvertimeHours}
                                standardBillableHours={v.totalBillableRegularHours}
                                standardNotBillableHours={v.totalNotBillableRegularHours}
                              />
                            </div>
                          </div>
                          {usersToExpand[index][key] &&
                            Object.entries(v.tableData).map(([key, value]) => {
                              return <UserTable data={value} date={key} key={key} />
                            })}
                        </>
                      )
                    })}
                </div>
              )
            })}
        </>
      ) : (
        <div className={s.noData}>No data. Please check your filters.</div>
      )}
    </div>
  )
}

export default ReportByWorkType
